import { useState, useCallback } from "react";
import EnhancedCampaignHistory from "../campaigns/EnhancedCampaignHistory";
import EnhancedTemplateHistory from "../templates/EnhancedTemplateHistory";
import { PageContainer } from "../ui/page-container";
import HistoryHeader from "./HistoryHeader";
import { UserRole } from "./shared/RoleSwitcher";
import { Megaphone, FileText } from "lucide-react";

const tabs = [
    {
        id: "campaigns",
        label: "Campaign History",
        icon: Megaphone,
        accent: "from-amber-400 to-orange-500",
        iconBg: "bg-amber-50 dark:bg-amber-900/20",
        iconColor: "text-amber-600 dark:text-amber-400",
        activeText: "text-amber-600 dark:text-amber-400",
        activeBorder: "border-amber-200 dark:border-amber-700/40",
        dot: "bg-amber-400",
        desc: "Send events, A/B tests, optimisations",
    },
    {
        id: "templates",
        label: "Template History",
        icon: FileText,
        accent: "from-indigo-400 to-indigo-500",
        iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
        iconColor: "text-indigo-600 dark:text-indigo-400",
        activeText: "text-indigo-600 dark:text-indigo-400",
        activeBorder: "border-indigo-200 dark:border-indigo-700/40",
        dot: "bg-indigo-400",
        desc: "Edits, versions and collaborations",
    },
];

const CAMPAIGN_PERMISSIONS = [
    "view_own_campaigns",
    "view_assigned_campaigns",
    "view_performance_metrics",
    "view_team_campaigns",
    "view_analytics",
    "export_data",
    "view_all_campaigns",
    "view_system_data",
    "manage_campaigns",
];

const TEMPLATE_PERMISSIONS = [
    "view_own_templates",
    "view_assigned_templates",
    "view_collaboration",
    "view_team_templates",
    "view_template_analytics",
    "view_analytics",
    "export_templates",
    "view_all_templates",
    "view_system_data",
    "manage_templates",
];

const HistoryView = () => {
    const [activeTab, setActiveTab] = useState("campaigns");
    const [campaignRole, setCampaignRole] = useState<UserRole>("admin");
    const [templateRole, setTemplateRole] = useState<UserRole>("admin");

    const handleCampaignEventClick = useCallback((event: any) =>
        console.log("Campaign event:", event), []);

    const handleTemplateEventClick = useCallback((event: any) =>
        console.log("Template event:", event), []);

    const tab = tabs.find((t) => t.id === activeTab)!;

    return (
        <PageContainer>
            <HistoryHeader />
            <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-sm overflow-hidden">
                <div className={`h-[2px] w-full bg-gradient-to-r ${tab.accent} transition-all duration-300`} />
                <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-line-subtle dark:border-dark-700">
                    <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-dark-700/60 rounded-lg p-1 flex-shrink-0">
                        {tabs.map((t) => {
                            const Icon = t.icon;
                            const isActive = activeTab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveTab(t.id)}
                                    className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-[13px] font-semibold
                                               transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                                               ${isActive
                                            ? `bg-surface dark:bg-dark-800 shadow-sm ${t.activeText} border ${t.activeBorder}`
                                            : "text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white/60 dark:hover:bg-dark-800/40"
                                        }`}
                                >
                                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${isActive ? t.iconBg : ""}`}>
                                        <Icon className={`w-3 h-3 ${isActive ? t.iconColor : "opacity-50"}`} strokeWidth={2.2} />
                                    </div>
                                    {t.label}
                                    {isActive && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.dot}`} />}
                                </button>
                            );
                        })}
                    </div>
                </div>

            </div>
            <div>
                {activeTab === "campaigns" && (
                    <EnhancedCampaignHistory
                        onEventClick={handleCampaignEventClick}
                        userRole={campaignRole}
                        currentRole={campaignRole}
                        onRoleChange={setCampaignRole}
                        userPermissions={CAMPAIGN_PERMISSIONS}
                        enableRealTimeUpdates={true}
                        className="w-full"
                    />
                )}
                {activeTab === "templates" && (
                    <EnhancedTemplateHistory
                        onEventClick={handleTemplateEventClick}
                        userRole={templateRole}
                        currentRole={templateRole}
                        onRoleChange={setTemplateRole}
                        userPermissions={TEMPLATE_PERMISSIONS}
                        enableRealTimeUpdates={true}
                        className="w-full"
                    />
                )}
            </div>

        </PageContainer>
    );
};

export default HistoryView;
